import { Form, TableProps, Image, Tag } from 'antd';
import { useForm } from 'antd/es/form/Form';
import TableComponent from 'components/Table/TableComponent';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import React, { forwardRef, memo, useImperativeHandle } from 'react';
import { HocChangePagination } from 'utils/HocChangePagination';
import formatNumber, {
  formatCurrency,
  generateUUID,
} from 'utils/constants/functions';

interface IProps extends React.PropsWithChildren<TableProps> {
  data: any;
  refetch: (isExport?: boolean, headerRow?: any) => void;
}

const TablePaymentInfo = forwardRef((props: IProps, ref: any) => {
  const { data = {}, loading, refetch } = props;
  const [form] = useForm();

  const columns: ColumnsTypeCustom = [
    {
      title: 'STT',
      width: 60,
      align: 'center',
      fixed: 'left',
      render: (_, __, index) =>
        (data.pageInfo?.itemsPerPage || 20) *
          ((data.pageInfo?.currentPage || 1) - 1) +
        index +
        1,
    },
    {
      title: 'Mã công ty',
      width: 110,
      dataIndex: 'companyCode',
    },
    {
      title: 'Chi nhánh',
      width: 100,
      dataIndex: 'plantId',
    },
    {
      title: 'Loại SO',
      width: 100,
      dataIndex: 'orderType',
    },
    {
      title: 'Mã bảng kê DMS / Thu dẻ DMS',
      width: 180,
      dataIndex: 'dmsOrderCode',
    },
    {
      title: 'Bảng Tổng Hợp Thanh Toán',
      width: 170,
      dataIndex: 'sumPaymentId',
    },
    {
      title: 'Mã Khách hàng',
      width: 140,
      dataIndex: 'customerId',
    },
    {
      title: 'Ngày tạo',
      width: 120,
      dataIndex: 'createDate',
    },
    {
      title: 'Tiền trả',
      width: 130,
      dataIndex: 'paidAmount',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Trạng thái thanh toán',
      width: 170,
      align: 'center',
      dataIndex: 'paymentStatus',
      render: (val: any) => {
        if (!val || val === '-') return '-';
        const statusStr = val;
        let color = 'success';
        if (statusStr === 'Thanh toán 1 phần') {
          color = 'warning';
        } else if (statusStr === 'Thất Bại' || statusStr === 'FAILED') {
          color = 'error';
        } else if (
          statusStr === 'Chưa thanh toán' ||
          statusStr === 'Chờ thanh toán'
        ) {
          color = 'default';
        } else if (statusStr === 'Công nợ') {
          color = 'processing';
        }
        return <Tag color={color}>{statusStr}</Tag>;
      },
    },
    {
      title: 'Trạng thái gửi (SAP)',
      width: 160,
      align: 'center',
      dataIndex: 'sapStatus',
      render: (val: any) => {
        if (!val || val === '-') return '-';
        const st = val || 'PENDING';
        if (st === 'SUCCESS' || st === 'Thành công')
          return <Tag color="success">Thành công</Tag>;
        if (st === 'PARTIAL_SUCCESS' || st === 'PARTIAL' || st === 'Đã gửi SAP')
          return <Tag color="warning">Đã gửi SAP</Tag>;
        if (st === 'FAILED' || st === 'Thất bại')
          return <Tag color="error">Thất bại</Tag>;
        return <Tag color="default">Chưa gửi</Tag>;
      },
    },
    {
      title: 'Thời gian thanh toán',
      width: 180,
      align: 'center',
      dataIndex: 'paymentDate',
      render: (val: any) => val || '-',
    },
    {
      title: 'Thời gian gửi SAP',
      width: 180,
      align: 'center',
      dataIndex: 'sapDate',
      render: (val: any) => val || '-',
    },
    {
      title: 'SO/PO',
      width: 130,
      dataIndex: 'soPo',
    },
    {
      title: 'Q10',
      width: 130,
      dataIndex: 'q10',
      align: 'right',
      render: (value) =>
        value ? formatCurrency({ value: Number(value), format: 4 }) : '0,0000',
    },
    {
      title: 'Ngày Bill/Invoice',
      width: 150,
      dataIndex: 'billDate',
    },
    {
      title: 'Mã Bill/Mã Miro',
      width: 140,
      dataIndex: 'billCode',
    },
    {
      title: 'HĐĐT',
      width: 220,
      dataIndex: 'hddt',
      render: (value: any, record: any) => {
        const link = record?.hddtLink || value;
        if (!link) return '';
        const href = String(link).startsWith('http')
          ? link
          : `https://hoadon.pnj.com.vn/?search=${encodeURIComponent(link)}`;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#1890ff',
              textDecoration: 'underline',
              fontWeight: 500,
              wordBreak: 'break-all',
            }}
          >
            {link}
          </a>
        );
      },
    },
    {
      title: 'Mã chứng từ FI',
      width: 140,
      dataIndex: 'flQ10',
    },
    {
      title: 'TL Q10',
      width: 120,
      dataIndex: 'tlQ10',
      align: 'right',
      render: (value: any) =>
        value ? formatCurrency({ value: Number(value), format: 4 }) : '0,0000',
    },
    {
      title: 'GT VND',
      width: 140,
      dataIndex: 'gtVnd',
      align: 'right',
      render: (value: any) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'FI Công',
      width: 120,
      dataIndex: 'flCong',
      render: (value: any, record: any) => {
        const isDe = String(record?.dmsOrderCode).startsWith('TD');
        if (record?.isHangMon || isDe) {
          return '-';
        }
        return value || '-';
      },
    },
    {
      title: 'Công VND',
      width: 140,
      dataIndex: 'congVnd',
      align: 'right',
      render: (value: any, record: any) => {
        const isDe = String(record?.dmsOrderCode).startsWith('TD');
        if (record?.isHangMon || isDe) {
          return '-';
        }
        return value ? formatNumber(value) : '0';
      },
    },
    {
      title: 'Tổng VND',
      width: 140,
      dataIndex: 'tongVnd',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Tiền mặt',
      width: 130,
      dataIndex: 'tienMat',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : ''),
    },
    {
      title: 'Công nợ',
      width: 130,
      dataIndex: 'congNo',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'GT PO Cấn trừ',
      width: 140,
      dataIndex: 'gtPoCanTru',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Tổng GT đã thu',
      width: 150,
      dataIndex: 'tongGtDaThu',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Chuyển Khoản',
      width: 140,
      dataIndex: 'chuyenKhoan',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'GT Còn lại',
      width: 130,
      dataIndex: 'gtConLai',
      align: 'right',
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Mã QRCODE 1',
      width: 120,
      align: 'center',
      dataIndex: 'qr1',
      render: (value) => {
        if (!value) return '-';
        const qrUrl = value.startsWith('data:image')
          ? value
          : `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
              value,
            )}`;
        return <Image src={qrUrl} width={60} height={60} preview />;
      },
    },
    {
      title: 'TTTT QR1',
      width: 140,
      align: 'center',
      dataIndex: 'qr1Status',
      render: (val: any, record: any) => {
        if (!record?.qr1) return '-';
        const isPaid = record?.qr1IsPaid || val === 'Đã thanh toán';
        return (
          <Tag color={isPaid ? 'success' : 'warning'}>
            {val || (isPaid ? 'Đã thanh toán' : 'Chưa thanh toán')}
          </Tag>
        );
      },
    },
    {
      title: 'Mã QRCODE 2',
      width: 120,
      align: 'center',
      dataIndex: 'qr2',
      render: (value) => {
        if (!value) return '-';
        const qrUrl = value.startsWith('data:image')
          ? value
          : `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
              value,
            )}`;
        return <Image src={qrUrl} width={60} height={60} preview />;
      },
    },
    {
      title: 'TTTT QR2',
      width: 140,
      align: 'center',
      dataIndex: 'qr2Status',
      render: (val: any, record: any) => {
        if (!record?.qr2) return '-';
        const isPaid = record?.qr2IsPaid || val === 'Đã thanh toán';
        return (
          <Tag color={isPaid ? 'success' : 'warning'}>
            {val || (isPaid ? 'Đã thanh toán' : 'Chưa thanh toán')}
          </Tag>
        );
      },
    },
    {
      title: 'Mã QRCODE 3',
      width: 120,
      align: 'center',
      dataIndex: 'qr3',
      render: (value) => {
        if (!value) return '-';
        const qrUrl = value.startsWith('data:image')
          ? value
          : `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
              value,
            )}`;
        return <Image src={qrUrl} width={60} height={60} preview />;
      },
    },
    {
      title: 'TTTT QR3',
      width: 140,
      align: 'center',
      dataIndex: 'qr3Status',
      render: (val: any, record: any) => {
        if (!record?.qr3) return '-';
        const isPaid = record?.qr3IsPaid || val === 'Đã thanh toán';
        return (
          <Tag color={isPaid ? 'success' : 'warning'}>
            {val || (isPaid ? 'Đã thanh toán' : 'Chưa thanh toán')}
          </Tag>
        );
      },
    },
    {
      title: 'Mã Thanh Toán',
      width: 150,
      dataIndex: 'ctReference',
    },
    {
      title: 'Ref.Bank',
      width: 220,
      dataIndex: 'refBank',
    },
    {
      title: 'Thông tin trên QR',
      width: 240,
      dataIndex: 'qrInfo',
    },
  ];

  useImperativeHandle(ref, () => ({
    exportExcel,
  }));

  const exportExcel = () => {
    const exportColumns = columns.map((col: any) => ({
      title: col.title,
      dataIndex: col.dataIndex,
      width: col.width,
    }));
    refetch(true, exportColumns);
  };

  return (
    <Content>
      <Form form={form}>
        <TableComponent
          scroll={{ x: 'max-content', y: 'calc(100vh - 350px)' }}
          columns={columns}
          dataSource={data?.items || []}
          rowKey={(record: any) => record?.id || generateUUID()}
          loading={loading}
          pagination={{
            current: data?.pageInfo?.currentPage || 1,
            pageSize: data?.pageInfo?.itemsPerPage || 20,
            total: data?.pageInfo?.totalItems || 0,
            onChange: HocChangePagination(),
          }}
        />
      </Form>
    </Content>
  );
});

export default memo(TablePaymentInfo);
